import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import type { PermissionLevel } from '@/types/api'

export interface User {
  id: string
  username: string
  email: string
  full_name?: string
  role: string
  is_active: boolean
  is_admin?: boolean
  created_at?: string
  updated_at?: string
}

export type PermissionsMap = Record<string, PermissionLevel>

interface AuthState {
  token: string | null
  user: User | null
  isAuthenticated: boolean
  /** Per-function RBAC level (Edit > View > NoGrant). Empty = treat per RoleGuard. */
  permissions: PermissionsMap
}

/** Safe JSON.parse cho localStorage — nuốt cả raw string `"undefined"` /
 *  `"null"` / giá trị corrupt (`JSON.parse('undefined')` throws). */
const _safeParse = <T>(raw: string | null, fallback: T): T => {
  if (raw == null || raw === '' || raw === 'undefined' || raw === 'null') return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

const _readPermissions = (): PermissionsMap =>
  _safeParse<PermissionsMap>(localStorage.getItem('permissions'), {})

const initialState: AuthState = {
  token: localStorage.getItem('token'),
  user: _safeParse<User | null>(localStorage.getItem('user'), null),
  isAuthenticated: !!localStorage.getItem('token'),
  permissions: _readPermissions(),
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ token: string; user: User | null }>) => {
      state.token = action.payload.token
      state.user = action.payload.user
      state.isAuthenticated = true
      localStorage.setItem('token', action.payload.token)
      // Luôn sync localStorage: payload.user nullish → remove để tránh
      // raw `"undefined"` còn sót (JSON.parse "undefined" throws).
      if (action.payload.user) {
        localStorage.setItem('user', JSON.stringify(action.payload.user))
      } else {
        localStorage.removeItem('user')
      }
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
      localStorage.setItem('user', JSON.stringify(action.payload))
    },
    /** Trình diễn: đặt vai trò không cần đăng nhập (tương đương đăng nhập trong bản production). */
    setDemoUser: (state, action: PayloadAction<string>) => {
      const role = action.payload
      const fullNames: Record<string, string> = {
        user: 'Phạm Thị Hồng Nhung',
        litigant: 'Phạm Thị Hồng Nhung',
        secretary: 'Nguyễn Thị Hương',
        judge: 'Trần Văn Bình',
        admin: 'Lê Văn Cường',
        chief_judge: 'Lê Văn Cường',
      }
      state.token = 'demo-token'
      state.user = {
        id: 'demo-user',
        username: role === 'user' || role === 'litigant' ? 'pham.nhung' : role === 'secretary' ? 'nguyen.huong' : role === 'judge' ? 'tran.binh' : 'le.cuong',
        email: role === 'user' || role === 'litigant' ? 'pham.nhung@email.vn' : role === 'secretary' ? 'nguyen.huong@toaan.vn' : role === 'judge' ? 'tran.binh@toaan.vn' : 'le.cuong@toaan.vn',
        full_name: fullNames[role] || role,
        role,
        is_active: true,
      }
      state.isAuthenticated = true
      localStorage.setItem('token', state.token)
      localStorage.setItem('user', JSON.stringify(state.user))
    },
    setPermissions: (state, action: PayloadAction<PermissionsMap>) => {
      state.permissions = action.payload
      localStorage.setItem('permissions', JSON.stringify(action.payload))
    },
    logout: (state) => {
      state.token = null
      state.user = null
      state.isAuthenticated = false
      state.permissions = {}
      localStorage.clear()
    },
  },
})

export const { setCredentials, setUser, setDemoUser, setPermissions, logout } = authSlice.actions
/** Alias for synced code that imports `clearAuth`. */
export const clearAuth = logout
export default authSlice.reducer
